import { Divider, Radio, type RadioChangeEvent, Tooltip } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import InfoIcon from '@/assets/icons/info-i.svg?react'
import NewebPayDarkImg from '@/assets/images/payment-newebpay-dark.svg?react'
import NewebPayImg from '@/assets/images/payment-newebpay.svg?react'
import PaypalDarkImg from '@/assets/images/payment-paypal-dark.svg?react'
import PaypalImg from '@/assets/images/payment-paypal.svg?react'
import StripeDarkImg from '@/assets/images/payment-stripe-dark.svg?react'
import StripeImg from '@/assets/images/payment-stripe.svg?react'
import { Loading } from '@/components'
import { PAYMENT_TYPE } from '@/consts/billing'
import { useThemeMode } from '@/contexts/ThemeContext'
import { formatPrice } from '@/utils/formatter'

interface PaymentMethodSelectorProps {
  currency: string
  disabled?: boolean
  value?: number
  onChange?: (e: RadioChangeEvent) => void
  // Platform balance is only valid for general team recharges
  allowPlatformBalanceTransfer?: boolean
  platformBalance?: number // in cents
  isPlatformBalanceLoading?: boolean
}

export function PaymentMethodSelector({
  currency,
  disabled = false,
  value,
  onChange,
  allowPlatformBalanceTransfer = false,
  platformBalance = 0,
  isPlatformBalanceLoading = false,
}: PaymentMethodSelectorProps) {
  const { t } = useTranslation('billing')
  const { isDark } = useThemeMode()
  const [stripeVisible, setStripeVisible] = useState(false)
  const [newebPayVisible, setNewebPayVisible] = useState(false)
  const [paypalVisible, setPaypalVisible] = useState(false)
  const NewebPayLogo = isDark ? NewebPayDarkImg : NewebPayImg
  const PaypalLogo = isDark ? PaypalDarkImg : PaypalImg
  const StripeLogo = isDark ? StripeDarkImg : StripeImg

  return (
    <Radio.Group
      className="w-full grid grid-cols-1 gap-1 border border-gray100 rounded-lg px-4 py-1"
      value={value}
      onChange={onChange}
    >
      {/* Transferred credits cannot fund subscriptions. */}
      {allowPlatformBalanceTransfer && (
        <>
          <Radio
            value={PAYMENT_TYPE.GLOWS_PLATFORM_BALANCE}
            className="flex-1 flex items-center py-3"
            disabled={disabled || platformBalance <= 0 || currency !== 'USD'}
            onMouseEnter={() => currency !== 'USD'}
            onMouseLeave={() => currency !== 'USD'}
          >
            <div className="flex items-center flex-1 gap-2 justify-between">
              <div className="t-bold">Glows.ai Balance</div>
              <div className="text-secondary t-sm px-4 py-1 rounded-full bg-neutral-50">
                {isPlatformBalanceLoading ? (
                  <Loading className="h-4 w-4" />
                ) : (
                  <div className="flex gap-2">
                    <div className="text-highlight">{formatPrice(platformBalance, false, '')}</div>
                    <div className="text-secondary">
                      {platformBalance > 100 ? 'Credits' : 'Credit'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Radio>
          <Divider className="my-0 bg-neutral-200"></Divider>
        </>
      )}

      <Radio
        value={PAYMENT_TYPE.NEWEBPAY}
        className="flex-1 flex items-center py-3"
        disabled={currency !== 'TWD' || disabled}
        onMouseEnter={() => (currency !== 'TWD' || disabled ? setNewebPayVisible(true) : null)}
        onMouseLeave={() => (currency !== 'TWD' || disabled ? setNewebPayVisible(false) : null)}
      >
        <div className="flex items-center flex-1 gap-2">
          <NewebPayLogo width={146} height={20} />
          {currency === 'TWD' && !disabled && (
            <Tooltip title={t('paymentMethod.twdOnly')} placement="top">
              <span className="inline-flex">
                <InfoIcon className="w-4 h-4 flex-shrink-0" />
              </span>
            </Tooltip>
          )}
          {(currency !== 'TWD' || disabled) && (
            <Tooltip title={t('paymentMethod.twdOnly')} placement="top" open={newebPayVisible}>
              <span className="inline-flex">
                <InfoIcon className="w-4 h-4 flex-shrink-0" />
              </span>
            </Tooltip>
          )}
        </div>
      </Radio>

      <Divider className="my-0 bg-neutral-200"></Divider>

      <Radio
        value={PAYMENT_TYPE.PAYPAL}
        className="flex-1 flex items-center py-3"
        disabled={currency !== 'USD' || disabled}
        onMouseEnter={() => (currency !== 'USD' || disabled ? setPaypalVisible(true) : null)}
        onMouseLeave={() => (currency !== 'USD' || disabled ? setPaypalVisible(false) : null)}
      >
        <div className="flex items-center flex-1 gap-2">
          <PaypalLogo width={82} height={20} />
          {currency === 'USD' && !disabled && (
            <Tooltip title={t('paymentMethod.usdOnly')} placement="top">
              <span className="inline-flex">
                <InfoIcon className="w-4 h-4 flex-shrink-0" />
              </span>
            </Tooltip>
          )}
          {(currency !== 'USD' || disabled) && (
            <Tooltip title={t('paymentMethod.usdOnly')} placement="top" open={paypalVisible}>
              <span className="inline-flex">
                <InfoIcon className="w-4 h-4 flex-shrink-0" />
              </span>
            </Tooltip>
          )}
        </div>
      </Radio>

      <Divider className="my-0 bg-neutral-200"></Divider>

      <Radio
        value={PAYMENT_TYPE.STRIPE}
        className="flex-1 flex items-center py-3"
        disabled={currency !== 'USD' || disabled}
        onMouseEnter={() => (currency !== 'USD' || disabled ? setStripeVisible(true) : null)}
        onMouseLeave={() => (currency !== 'USD' || disabled ? setStripeVisible(false) : null)}
      >
        <div className="flex items-center flex-1 gap-2">
          <StripeLogo width={48} height={20} />
          {currency === 'USD' && !disabled && (
            <Tooltip title={t('paymentMethod.usdOnly')} placement="top">
              <span className="inline-flex">
                <InfoIcon className="w-4 h-4 flex-shrink-0" />
              </span>
            </Tooltip>
          )}
          {(currency !== 'USD' || disabled) && (
            <Tooltip title={t('paymentMethod.usdOnly')} placement="top" open={stripeVisible}>
              <span className="inline-flex">
                <InfoIcon className="w-4 h-4 flex-shrink-0" />
              </span>
            </Tooltip>
          )}
        </div>
      </Radio>
    </Radio.Group>
  )
}
